import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../modals/modal.component";
import { useElectron } from "../../hooks/useElectron";
import type { UpdateChannel, UpdateChannelInfo } from "../../types/electron/updates.types";

import "./updateChannel.styles.scss";

interface UpdateChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function UpdateChannelModal({ isOpen, onClose }: UpdateChannelModalProps) {
  const { t } = useTranslation();
  const { updates } = useElectron();

  const [channelInfo, setChannelInfo] = useState<UpdateChannelInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const loadChannelInfo = useCallback(async () => {
    if (!updates) return;
    setLoading(true);
    try {
      const info = await updates.getChannelInfo();
      setChannelInfo(info);
    } catch {
      setChannelInfo(null);
    } finally {
      setLoading(false);
    }
  }, [updates]);

  useEffect(() => {
    if (isOpen) {
      setFeedback(null);
      loadChannelInfo();
    }
  }, [isOpen, loadChannelInfo]);

  const handleChannelChange = async (channel: UpdateChannel) => {
    if (!updates || !channelInfo || channel === channelInfo.current) return;

    setSaving(true);
    setFeedback(null);
    try {
      const result = await updates.setChannel(channel);
      console.log("Set channel result:", result);
      if (result.success) {
        setChannelInfo((prev) => prev ? { ...prev, current: result.channel } : prev);
        setFeedback({ type: "success", message: t("channel.modal.success") });
      } else {
        setFeedback({ type: "error", message: t("channel.modal.disabled") });
      }
    } catch {
      setFeedback({ type: "error", message: t("channel.modal.error") });
    } finally {
      setSaving(false);
    }
  };

  const channelLabels: Record<UpdateChannel, string> = {
    prod: t("channel.modal.prod"),
    dev: t("channel.modal.dev"),
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} type="success">
      <div className="UpdateChannelModal">
        <div className="UpdateChannelModal__header">
          <h2>{t("channel.modal.title")}</h2>
          <p>{t("channel.modal.description")}</p>
        </div>

        {loading ? (
          <div className="UpdateChannelModal__loading">
            <span>{t("common.loading")}</span>
          </div>
        ) : channelInfo ? (
          <div className="UpdateChannelModal__content">
            <div className="UpdateChannelModal__info">
              <div className="UpdateChannelModal__info-row">
                <span className="label">{t("channel.modal.current")}:</span>
                <span className="value badge badge--current">{channelLabels[channelInfo.current]}</span>
              </div>
              <div className="UpdateChannelModal__info-row">
                <span className="label">{t("channel.modal.default")}:</span>
                <span className="value badge badge--default">{channelLabels[channelInfo.default]}</span>
              </div>
            </div>

            <div className="UpdateChannelModal__selector">
              <span className="label">{t("channel.modal.select")}:</span>
              <div className="UpdateChannelModal__channels">
                {channelInfo.availableChannels.map((ch) => (
                  <button
                    key={ch}
                    className={`channel-btn ${ch === channelInfo.current ? "channel-btn--active" : ""}`}
                    disabled={saving || ch === channelInfo.current}
                    onClick={() => handleChannelChange(ch)}
                  >
                    {channelLabels[ch]}
                  </button>
                ))}
              </div>
            </div>

            {feedback && (
              <div className={`UpdateChannelModal__feedback UpdateChannelModal__feedback--${feedback.type}`}>
                {feedback.message}
              </div>
            )}
          </div>
        ) : (
          <div className="UpdateChannelModal__error">
            <span>{t("channel.modal.error")}</span>
          </div>
        )}

        <div className="UpdateChannelModal__actions">
          <button className="close" onClick={onClose}>
            {t("button.modal.close")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
